import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Convert anything thrown in a try/catch into a human-readable string.
 *
 * The Supabase JS client rejects with a plain object ({ message, details,
 * hint, code }), not a real Error instance — so `String(err)` returns
 * "[object Object]" and `err instanceof Error` is false. This helper
 * unwraps the common shapes so the user sees the actual problem.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; hint?: unknown; details?: unknown; code?: unknown };
    if (typeof e.message === 'string' && e.message.length > 0) return e.message;
    if (typeof e.hint === 'string' && e.hint.length > 0) return e.hint;
    if (typeof e.details === 'string' && e.details.length > 0) return e.details;
    if (typeof e.code === 'string' && e.code.length > 0) return `Error code: ${e.code}`;
    try {
      return JSON.stringify(err);
    } catch {
      // fall through
    }
  }
  return String(err);
}

export const hasSupabaseConfig = Boolean(
  url && anon && !url.includes('your-project'),
);

let _client: SupabaseClient | null = null;

export function getDb(): SupabaseClient {
  if (!hasSupabaseConfig) {
    throw new Error(
      'Supabase env vars are missing. Copy .env.example to .env.local and fill in VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    );
  }
  if (!_client) {
    _client = createClient(url, anon, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        // Supabase Auth client stores the session under this storage key.
        storageKey: 'norush.auth.v1',
      },
    });
  }
  return _client;
}
